import { capitalizeExtra } from 'capitalize-lightweight';
import {
  IAllStatusInclAll,
  IOrderDeclined,
  IOrderShipped,
  IOrderStatus,
  IOrderWithStatus,
  IPreorder,
  IPreorderStatus,
  IPreorderWithStatus,
  IShippingEvent,
} from 'my-poppies-b2b-types';
import { useContext } from 'react';

import { colors } from '@/colors';
import { Container } from '@/components/Container';
import { KeyValuePair } from '@/components/KeyValuePair';
import { OrderPriority } from '@/components/Order/buildingBlocks/OrderPriority';
import { PreorderTypeBadge } from '@/components/Order/buildingBlocks/PreorderTypeBadge';
import { StatusIcon } from '@/components/StatusIcon';
import { TextWithHighlighting } from '@/components/TextWithHighlighting';
import { dotDelimiter, statusCategoryColorMapping } from '@/config';
import { LanguageContext } from '@/contexts/LanguageContext';
import { OrderContext } from '@/contexts/OrderContext';
import { SettingsContext } from '@/contexts/SettingsContext';
import useAllSearchParams from '@/hooks/useAllSearchParams';
import { useAppPush } from '@/hooks/useAppPush';
import useTranslation from '@/hooks/useTranslation';
import { prettifyTimestamp } from '@/utils/getNow';

interface IOrderInfoProps {
  order: IOrderWithStatus<IOrderStatus> | IPreorderWithStatus<IPreorderStatus>;
}

export const OrderInfo = ({ order }: IOrderInfoProps) => {
  const searchParams = useAllSearchParams();
  const statusFromPage = searchParams?.status as IAllStatusInclAll | undefined;
  const { push } = useAppPush();
  const { updatedAt, updatedBy, createdBy, id, createdAt, status } = order;
  const { erpId, customerPretty, priority, approvalDate } = order as IOrderShipped;
  const { type: preorderType } = order as IPreorder;
  const history = (order as IOrderShipped).history;
  const reason = (order as unknown as IOrderDeclined).reason;

  const { settings } = useContext(SettingsContext);
  const { setShowShippingStatusModalForOrder, orderFilters } = useContext(OrderContext);
  const statusCategoryMapping = settings?.statusCategoryMapping;
  const { currentLanguageWithFallback } = useContext(LanguageContext);
  const t = useTranslation();

  const qValue = orderFilters?.q ?? '';

  const showUpdatedData =
    Boolean(updatedBy) && Boolean(updatedAt) && (createdBy !== updatedBy || createdAt !== updatedAt);

  const showShippingStatus = Boolean(history) && Boolean(history?.[0]) && Boolean(history?.[0].shippingStatus);

  const orderStatusPretty = t(`order.status.${status}`);
  const OrderStatusPrettyFull = showShippingStatus
    ? () => {
        const shippingEvent: IShippingEvent = history![0]!; // TODO!!!! #12345
        const { shippingStatusPretty, shippingStatus } = shippingEvent;
        return (
          <Container
            sx={{
              margin: 'unset',
              width: 'unset',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              height: '100%',
            }}
          >
            {status !== 'DELIVERED' && (
              <>
                <span style={{ color: colors.black, marginRight: 4 }}>{orderStatusPretty}</span>
                {dotDelimiter}
              </>
            )}
            <span style={{ color: colors.black, marginRight: 4 }}>
              <StatusIcon size={24} status={shippingStatus} statusType={'SHIPPING_STATUS'} />
            </span>
            <span
              style={{
                color: !statusCategoryMapping
                  ? colors.black
                  : statusCategoryColorMapping[statusCategoryMapping[shippingStatus]],
                marginRight: 4,
              }}
            >
              {shippingStatusPretty![currentLanguageWithFallback]}
            </span>
            {dotDelimiter}
            <span
              onClick={() => {
                setShowShippingStatusModalForOrder(order as IOrderShipped);
              }}
              style={{ cursor: 'pointer', marginLeft: 4, color: 'blue', textDecoration: 'underline' }}
            >
              {t('order.track')}
            </span>
          </Container>
        );
      }
    : () => orderStatusPretty;

  const isAllOrdersPage = statusFromPage === 'ALL_ORDERS' || statusFromPage === 'ALL_OPEN_ORDERS';

  const ShowAllLink = () => {
    if (!isAllOrdersPage) {
      return null;
    }
    return (
      <Container
        onClick={() => {
          push(`/orders?status=${status as IOrderStatus}`);
        }}
        style={{ cursor: 'pointer', display: 'flex', fontSize: 12, textDecoration: 'underline' }}
      >
        {t('order.showAllInStatus', undefined, undefined, { status: t(`order.status.${status}`) })}
      </Container>
    );
  };

  return (
    <Container debugColor={'blue'} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Container
        debugColor={'lightblue'}
        sx={{
          fontWeight: 'bold',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          textAlign: 'left',
        }}
      >
        {priority && <OrderPriority priority={priority} />}
        {t(priority ? 'order.order' : status.includes('PREORDER') ? 'order.preOrder' : 'order.internalOrder')}
        <span
          className={'one-line-ellipsis'}
          style={{ color: 'white', backgroundColor: '#575757', padding: '0 4px', borderRadius: '4px', fontSize: 14 }}
        >
          {erpId ?? id}
        </span>
        {preorderType && <PreorderTypeBadge type={preorderType} />}
      </Container>
      <Container debugColor={'lightblue'} sx={{ display: 'flex', flexDirection: 'row', gap: '4px', textAlign: 'left' }}>
        <KeyValuePair
          addColon
          keyName={t('order.date')}
          value={prettifyTimestamp(createdAt, currentLanguageWithFallback)}
        />
      </Container>
      {createdBy && (
        <Container
          debugColor={'turquoise'}
          sx={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}
        >
          <KeyValuePair addColon keyName={t('general.createdBy')} value={capitalizeExtra(createdBy)} />
        </Container>
      )}
      {showUpdatedData && (
        <>
          <Container
            debugColor={'lightblue'}
            sx={{ display: 'flex', flexDirection: 'row', gap: '4px', textAlign: 'left' }}
          >
            <KeyValuePair
              addColon
              keyName={t('order.updatedAt')}
              value={prettifyTimestamp(updatedAt!, currentLanguageWithFallback)}
            />
          </Container>
          <Container
            debugColor={'turquoise'}
            sx={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}
          >
            <KeyValuePair addColon keyName={t('general.updatedBy')} value={capitalizeExtra(updatedBy!)} />
          </Container>
        </>
      )}
      {approvalDate && (
        <Container
          debugColor={'turquoise'}
          sx={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}
        >
          <KeyValuePair
            addColon
            keyName={t('order.approve.date')}
            value={
              <TextWithHighlighting searchValue={qValue}>
                {prettifyTimestamp(approvalDate, currentLanguageWithFallback)}
              </TextWithHighlighting>
            }
          />
        </Container>
      )}
      {customerPretty && (
        <Container
          debugColor={'turquoise'}
          sx={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}
        >
          <KeyValuePair
            addColon
            keyName={t('order.customer')}
            value={<TextWithHighlighting searchValue={qValue}>{customerPretty}</TextWithHighlighting>}
          />
        </Container>
      )}
      <Container debugColor={'lightblue'} sx={{ display: 'flex', flexDirection: 'row', gap: '4px', textAlign: 'left' }}>
        <KeyValuePair maxHeight={'18px'} addColon keyName={t('order.status')} value={<OrderStatusPrettyFull />} />
      </Container>
      {status === 'DECLINED' && reason && (
        <Container
          debugColor={'lightblue'}
          sx={{ display: 'flex', flexDirection: 'row', gap: '4px', textAlign: 'left' }}
        >
          <KeyValuePair maxHeight={'18px'} addColon keyName={t('order.declined.reason')} value={reason} />
        </Container>
      )}
      <ShowAllLink />
    </Container>
  );
};
